import { useState } from 'react';
import { ChevronDown, ChevronUp, History } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
	ACTOR_CLASSES,
	ACTOR_LABELS,
	formatDateTime,
	formatTime,
} from '../sandboxLabels';

interface Props {
	events: Sandbox.Event[];
}

/** Linha do tempo do cenário: cada ação simulada, com o ator e o payload. */
const SandboxTimeline = ({ events }: Props) => {
	const [openId, setOpenId] = useState<string | null>(null);

	return (
		<div className="rounded-2xl border border-stone-100 bg-white p-4 shadow-sm">
			<div className="flex items-center gap-2">
				<History className="h-4 w-4 text-stone-400" />
				<p className="text-[11px] font-black uppercase tracking-wide text-stone-400">
					Linha do tempo
				</p>
				<span className="ml-auto text-xs text-stone-400">
					{events.length} evento(s)
				</span>
			</div>

			{events.length === 0 ? (
				<p className="mt-4 text-sm text-stone-400">
					Nada aconteceu ainda. Use os botões de simulação.
				</p>
			) : (
				<ol className="mt-4 max-h-[70vh] space-y-3 overflow-y-auto pr-1">
					{events.map((event) => {
						const isOpen = openId === event.id;
						const hasPayload =
							event.payload &&
							Object.keys(event.payload).length > 0;
						return (
							<li
								key={event.id}
								className="border-l-2 border-stone-100 pl-3"
							>
								<div className="flex flex-wrap items-center gap-2">
									<span
										className={cn(
											'rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wide',
											ACTOR_CLASSES[event.actor] ??
												ACTOR_CLASSES.SYSTEM,
										)}
									>
										{ACTOR_LABELS[event.actor] ??
											event.actor}
									</span>
									<span
										className="text-[11px] text-stone-400"
										title={formatDateTime(event.created_at)}
									>
										{formatTime(event.created_at)}
									</span>
								</div>
								<p className="mt-1 text-xs leading-relaxed text-stone-700">
									{event.summary}
								</p>
								{hasPayload ? (
									<button
										type="button"
										onClick={() =>
											setOpenId(isOpen ? null : event.id)
										}
										className="mt-1 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wide text-stone-400 hover:text-stone-900"
									>
										{isOpen ? (
											<ChevronUp className="h-3 w-3" />
										) : (
											<ChevronDown className="h-3 w-3" />
										)}
										{event.action}
									</button>
								) : (
									<span className="mt-1 block text-[10px] font-black uppercase tracking-wide text-stone-300">
										{event.action}
									</span>
								)}
								{isOpen && hasPayload ? (
									<pre className="mt-2 max-h-48 overflow-auto rounded-xl bg-stone-900 p-3 text-[11px] leading-relaxed text-stone-100">
										{JSON.stringify(event.payload, null, 2)}
									</pre>
								) : null}
							</li>
						);
					})}
				</ol>
			)}
		</div>
	);
};

export { SandboxTimeline };
